/**
 * /image-music — music from a picture, via Lyria 3 Pro's multimodal input.
 *
 * ============================================================================
 * WHAT IS REAL HERE AND WHAT IS NOT — read before changing the copy.
 *
 * Real: choosing the image, the preview, replacing and removing it, the prompt,
 * the composer options, the validation, and the Lyria 3 request that gets built
 * from all of it. `generationApi.buildImageMusicRequest` produces the exact
 * documented body — the selected provider model, a text block, then image blocks
 * with `mime_type` and base64 `data` — and it is unit tested (api/__tests__/
 * lyria.test.ts).
 *
 * Not real in mock/test builds: sending it. Without a Lyrixa backend there is nowhere
 * to post the request, so the button is disabled, the reason is on screen, and
 * NOTHING here fabricates a song. The rest of the app openly simulates generation with
 * a null `audioUrl`; Image Music was asked for as a real Lyria 3 flow, and a fake one
 * would be worse than an honest refusal.
 *
 * In runtime/backend mode this screen uses the same generation funnel as every other
 * creation path and posts its image-backed request to `/generations`. The backend owns
 * the Google credentials and can still fail fast if it is not configured for Lyria 3.
 *
 * The moment a backend is configured this screen needs no redesign: `imageMusicSupport()`
 * flips, the notice disappears, and the Generate button calls the funnel it already
 * calls.
 * ============================================================================
 *
 * WHY THE DESCRIPTION IS OPTIONAL
 * -------------------------------
 * The image is the compulsory input on this route. The text box is additive direction:
 * when it is empty the request still carries the image plus the shared genre / vocal /
 * language choices. That is why the description can now stay collapsed by default and
 * generation can still proceed once an image exists.
 *
 * REUSE, not reimplementation:
 *   lib/imageUpload      validation, the 8MB limit, the data-URL read
 *   CoverPreview's shape the label-wrapped hidden input, drag/drop, e.target.value = ''
 *   Composer             the prompt textarea and the genre/vocal/language choices
 *   ProcessingWave       the same five-second wave every other creation path shows
 *   useSongs().createSong the one generation funnel, which owns the credit and the wave
 *   createStore          so a prompt typed here survives navigation, as on /create
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Image as ImageIcon, Trash2, Upload } from 'lucide-react';
import type { CSSProperties } from 'react';
import {
  Composer,
  COMPOSER_ICONS,
  GenerationStatus,
  StudioHeader,
  TrackResult,
} from '../components/studio';
import { IconButton, PrototypeNotice, Tooltip } from '../components/ui';
import { generationApi } from '../api/generationApi';
import { base64FromDataUrl } from '../api/lyria';
import type { LyriaImage } from '../api/lyria';
import { GENRES, LANGUAGES, VOCALS } from '../data/music';
import { usePlayer, useSongs } from '../hooks';
import {
  ACCEPTED_IMAGE_LABEL,
  ACCEPT_ATTRIBUTE,
  MAX_IMAGE_LABEL,
  firstImageFrom,
  readImageAsDataUrl,
  validateImageFile,
} from '../lib/imageUpload';
import { MUSIC_MODEL_OPTIONS, STANDARD_MUSIC_MODEL } from '../lib/musicGenerationModels';
import { MUSIC_GENERATION_CREDITS } from '../lib/credits';
import { useCreateStore } from '../stores/createStore';
import { useEntitlementsStore } from '../stores/entitlementsStore';
import { useUiStore } from '../stores/uiStore';
import { c } from '../styles/theme';
import { paths } from './paths';

/** Credits an image generation costs. The same as a text generation — one song. */
const GENERATION_COST = MUSIC_GENERATION_CREDITS;

/** The stable route key this surface uses for generation and result ownership. */
const IMAGE_MUSIC_SURFACE = 'imageMusic' as const;

/**
 * The chosen image, as this screen holds it.
 *
 * `dataUrl` for the preview (an <img> needs the full URL) and `mimeType` + the bare
 * base64 for the request. Both derive from one read, so the picture on screen and the
 * bytes in the body cannot be different files.
 */
interface StagedImage {
  dataUrl: string;
  mimeType: string;
  filename: string;
}

/** Fills the preview box. Shared by the gradient placeholder and the <img>. */
const FILL: CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  display: 'block',
  borderRadius: 9,
};

/** The staged image as the API boundary wants it. */
function toLyriaImage(image: StagedImage): LyriaImage {
  return {
    data: base64FromDataUrl(image.dataUrl),
    mimeType: image.mimeType,
    filename: image.filename,
  };
}

export default function ImageMusic() {
  const navigate = useNavigate();

  /*
    Image Music keeps its own prompt text, because the user asked that words typed on
    /create must not appear here. The style choices stay shared: they are parameters of
    the same generation system, while the free-text direction belongs to the route the
    user is actively using.
  */
  const prompt = useCreateStore((s) => s.imageMusic.prompt);
  const model = useCreateStore((s) => s.model);
  const genre = useCreateStore((s) => s.genre);
  const vocal = useCreateStore((s) => s.vocal);
  const language = useCreateStore((s) => s.language);
  const resultSongId = useCreateStore((s) => s.resultSongIds.imageMusic);
  const image = useCreateStore((s) => s.imageMusic.image);
  const promptOpen = useCreateStore((s) => s.imageMusic.promptOpen);
  const setPrompt = useCreateStore((s) => s.setImageMusicPrompt);
  const setModel = useCreateStore((s) => s.setModel);
  const setGenre = useCreateStore((s) => s.setGenre);
  const setVocal = useCreateStore((s) => s.setVocal);
  const setLanguage = useCreateStore((s) => s.setLanguage);
  const setSurfaceResultSongId = useCreateStore((s) => s.setSurfaceResultSongId);
  const setImage = useCreateStore((s) => s.setImageMusicImage);
  const setPromptOpen = useCreateStore((s) => s.setImageMusicPromptOpen);
  const ownsProfessional = useEntitlementsStore((s) => s.professional);

  /** True while a file is dragged over the box. Presentation only. */
  const [over, setOver] = useState(false);
  /** True while the file is being read. A large image is not instant. */
  const [reading, setReading] = useState(false);

  const { generating, activeGenerationSurface, createSong, downloadSong, downloadLyrics, getSong } =
    useSongs();
  const { isPlaying, isActive, elapsed, bufferedSec, speed, loading, toggle, seek } = usePlayer();
  const setDeleteTarget = useUiStore((s) => s.setDeleteTarget);
  const showToast = useUiStore((s) => s.showToast);

  /*
    What the boundary says it can do. Read from the API rather than written here, so
    this screen cannot claim more or less than generationApi will actually attempt.
  */
  const support = generationApi.imageMusicSupport();

  // Same instrumental rule as /create: a track with no vocals has no language, and
  // generationApi overrules the field anyway.
  const instrumental = genre === 'Lo-fi instrumental' || vocal === 'Instrumental only';

  const generated = getSong(resultSongId ?? undefined);
  const ownsGeneratingUi = generating && activeGenerationSurface === IMAGE_MUSIC_SURFACE;
  const lockedByAnotherGeneration = generating && !ownsGeneratingUi;

  /**
   * Validates and reads a picked or dropped file.
   *
   * Every failure gets a toast. The brief's error list — upload fails, unsupported
   * type, too large, unreadable — is exactly what validateImageFile and
   * readImageAsDataUrl report, so there is nothing to invent here beyond showing it.
   */
  async function take(file: File | null) {
    if (!file) {
      showToast('That drop had no image in it');
      return;
    }
    const rejection = validateImageFile(file);
    if (rejection) {
      showToast(rejection.message);
      return;
    }
    setReading(true);
    try {
      const dataUrl = await readImageAsDataUrl(file);
      setImage({ dataUrl, mimeType: file.type, filename: file.name });
    } catch (error) {
      // A file can pass the type and size checks and still not decode — a truncated
      // download, or one removed from disk between picking and reading.
      showToast(error instanceof Error ? error.message : 'Could not read that image.');
    } finally {
      setReading(false);
    }
  }

  const ready = Boolean(image);

  return (
    <div>
      <StudioHeader
        title="Image Music"
        subtitle="Upload a picture and describe the music it should become."
      />

      {/* ---- the image ----
          A <label> wrapping a visually hidden file input, which is the pattern
          CoverPreview uses and works for the same reason: click, tap, Enter and Space
          all activate it natively, with no keydown handler of our own. Buttons stay
          OUTSIDE it — a button inside a label also opens the picker. */}
      <section aria-label="Reference image" style={{ marginBottom: 18 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            marginBottom: 8,
          }}
        >
          <p className="lyx-ws-eyebrow" style={{ margin: 0 }}>
            Reference image
          </p>
          {image && !reading && (
            <Tooltip label="Remove this image">
              <IconButton
                label="Remove reference image"
                nativeTitle={false}
                onClick={() => setImage(null)}
              >
                <Trash2 size={13} color={c.text2} />
              </IconButton>
            </Tooltip>
          )}
        </div>

        <label
          className="lyx-cover-preview"
          onDragOver={(e) => {
            // Required: without it the browser opens the dropped file and navigates
            // away from the app.
            e.preventDefault();
            if (!reading) setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setOver(false);
            if (!reading) void take(firstImageFrom(e.dataTransfer?.files ?? null));
          }}
          style={{
            background: over ? c.accentSoft : c.surface2,
            border: `1px ${image ? 'solid' : 'dashed'} ${
              over ? c.accentBorder : image ? c.border : c.borderStrong
            }`,
            cursor: reading ? 'default' : 'pointer',
          }}
        >
          <input
            type="file"
            accept={ACCEPT_ATTRIBUTE}
            disabled={reading}
            className="lyx-visually-hidden"
            aria-label={image ? 'Replace reference image' : 'Upload reference image'}
            onChange={(e) => {
              void take(firstImageFrom(e.target.files));
              // Cleared so picking the same file again fires onChange. Without this,
              // re-choosing a file after removing it silently does nothing.
              e.target.value = '';
            }}
          />

          {image ? (
            <img
              src={image.dataUrl}
              alt={`Reference image: ${image.filename}`}
              style={{ ...FILL, objectFit: 'cover' }}
            />
          ) : (
            <div className="lyx-cover-preview-empty">
              <ImageIcon size={20} color={over ? c.accent : c.text2} aria-hidden="true" />
              <p style={{ fontSize: 12.5, color: c.text1, margin: '10px 0 0', fontWeight: 500 }}>
                {reading ? 'Reading your image…' : 'Add an image'}
              </p>
              <p style={{ fontSize: 11.5, color: c.text2, margin: '3px 0 0', lineHeight: 1.5 }}>
                Click to choose a file or drop one here. {ACCEPTED_IMAGE_LABEL} · up to{' '}
                {MAX_IMAGE_LABEL}
              </p>
            </div>
          )}

          {image && !reading && (
            <span className="lyx-cover-hint" aria-hidden="true">
              <Upload size={11} />
              Click or drop to replace
            </span>
          )}
        </label>

        {image && (
          <p
            style={{
              fontSize: 11,
              color: c.text2,
              margin: '8px 0 0',
              fontFamily: 'IBM Plex Mono',
              wordBreak: 'break-all',
            }}
          >
            {image.filename} · {image.mimeType}
          </p>
        )}
      </section>

      {/* ---- the prompt and the options ----
          The style controls are shared with /create, but the free-text description is not:
          the user asked that typing on Create must not fill this box. It stays optional,
          collapsed by default, and persisted as part of Image Music's own draft state. */}
      <Composer
        prompt={prompt}
        onPromptChange={setPrompt}
        model={{
          label: 'Model',
          options: MUSIC_MODEL_OPTIONS.map((option) => ({
            value: option.value,
            label: option.label,
            locked: option.requiresOto2 && !ownsProfessional,
          })),
          value: ownsProfessional ? model : STANDARD_MUSIC_MODEL,
          onChange: setModel,
        }}
        promptLabel="Add an optional description for this image"
        promptPlaceholder="Add an optional description for this image"
        promptCollapsible
        promptToggleLabel="Optional description"
        promptOpen={promptOpen}
        onPromptOpenChange={setPromptOpen}
        primary={[
          {
            label: 'Genre',
            options: GENRES,
            value: genre,
            onChange: setGenre,
            clearable: true,
            icon: COMPOSER_ICONS.genre,
          },
        ]}
        advanced={[
          {
            label: 'Vocals',
            options: VOCALS,
            value: vocal,
            onChange: setVocal,
            icon: COMPOSER_ICONS.vocal,
          },
          {
            label: 'Language',
            options: LANGUAGES,
            value: language,
            onChange: setLanguage,
            icon: COMPOSER_ICONS.language,
            note: instrumental ? 'Not used for an instrumental track.' : undefined,
          },
        ]}
        onGenerate={async () => {
          if (!image) return;
          const song = await createSong(
            prompt,
            genre,
            GENERATION_COST,
            vocal,
            instrumental ? undefined : language,
            {
              images: [toLyriaImage(image)],
              ownerSurface: IMAGE_MUSIC_SURFACE,
              model: ownsProfessional ? model : STANDARD_MUSIC_MODEL,
            }
          );
          if (song) {
            setSurfaceResultSongId(IMAGE_MUSIC_SURFACE, song.id);
          }
        }}
        generating={ownsGeneratingUi}
        /*
          Off, but not pretending to work.

          `disabled` rather than folding these into `generating`: that prop also relabels
          the button "Generating…", and a button that says so while nothing is running is
          a false status — exactly the kind of thing this pass is meant to remove. Two
          reasons it can be off, and only one of them is local to this route now that the
          description is optional.
        */
        disabled={lockedByAnotherGeneration || !support.available || !ready}
        /* Short, and it does not repeat the selected model name. */
        disabledReason={
          !support.available
            ? 'Not available yet — see below.'
            : !ready
              ? 'Add a reference image first.'
              : lockedByAnotherGeneration
                ? 'Another generation is already in progress.'
                : undefined
        }
      />

      {/* ---- why the button is off ----
          The blockers come from the API boundary (`generationApi.imageMusicSupport()`), so
          this copy cannot drift from what the code actually checks. */}
      {!support.available && (
        <PrototypeNotice style={{ marginTop: 14 }}>
          <strong style={{ color: c.text1, fontWeight: 600 }}>
            Image Music is wired for Google music generation, but this build has no Lyrixsa backend
            configured to submit the request.
          </strong>{' '}
          {support.blockers.map((blocker) => blocker.summary).join(' ')} Everything up to the
          request is finished — once a backend is available, an image alone is enough to start.
        </PrototypeNotice>
      )}

      {/* One of these three at a time: working, the result, or nothing yet. The
          five-second wave is GenerationStatus, the same component the composer, the
          Genre Mixer and Music DNA show. */}
      {ownsGeneratingUi ? (
        <GenerationStatus />
      ) : generated ? (
        <TrackResult
          song={generated}
          playing={isPlaying(generated.id)}
          active={isActive(generated.id)}
          elapsed={elapsed}
          bufferedSec={bufferedSec}
          speed={speed}
          loading={loading && isActive(generated.id)}
          onTogglePlay={() => toggle(generated.id)}
          onSeek={(seconds) => seek(generated.id, seconds)}
          /* Navigates; the route gate refuses without the Studio plan. See router.tsx
             — the entitlement check is there rather than duplicated here. */
          onOpenStudio={() => navigate(paths.song(generated.id))}
          onDownload={() => downloadSong(generated)}
          onDownloadLyrics={() => downloadLyrics(generated)}
          onCopyLyrics={() => {
            navigator.clipboard?.writeText(generated.lyrics ?? '');
            showToast('Lyrics copied to clipboard');
          }}
          onDismiss={() => setSurfaceResultSongId(IMAGE_MUSIC_SURFACE, null)}
          onDelete={() => setDeleteTarget(generated, 'generatedResult')}
        />
      ) : null}
    </div>
  );
}
